function collectionHandleFromPathname(pathname) {
  const m = pathname.match(/\/collections\/([^/?#]+)/);
  return m ? m[1] : null;
}

/** Match Liquid: only filter.* params, sorted, stable for comparing facet URLs (incl. zero-result pages). */
function normalizedFilterQuery(search) {
  const raw = search && search.startsWith("?") ? search.slice(1) : search || "";
  return raw
    .split("&")
    .map((p) => p.trim())
    .filter((p) => p.includes("filter."))
    .map((p) => p.toLowerCase().replace(/%2b/g, "+").replace(/%252b/g, "+"))
    .sort()
    .join("&");
}

function hrefWithoutFilterParams() {
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  for (const key of [...new Set([...params.keys()])]) {
    if (key.startsWith("filter.")) {
      params.delete(key);
    }
  }
  const qs = params.toString();
  return `${url.pathname}${qs ? `?${qs}` : ""}`;
}

if (!customElements.get("secondary-nav")) {
  customElements.define(
    "secondary-nav",
    class SecondaryNav extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.scrollableElement = this.querySelector(
          ".secondary-navigation__ul",
        );
        this.container = this.querySelector(".secondary-navigation");

        this.checkScroll = this.checkScroll.bind(this);
        this.updateScrollableOverflow = this.updateScrollableOverflow.bind(this);
        this.handleContainerClick = this.handleContainerClick.bind(this);
        this.syncActiveLinks = this.syncActiveLinks.bind(this);

        if (this.scrollableElement && this.container) {
          this.scrollableElement.addEventListener("scroll", this.checkScroll);
        }

        if (
          this.container?.classList.contains("mobile-scroll-hint--fade") &&
          this.scrollableElement
        ) {
          if (typeof ResizeObserver !== "undefined") {
            this.resizeObserver = new ResizeObserver(() =>
              this.updateScrollableOverflow(),
            );
            this.resizeObserver.observe(this.scrollableElement);
          } else {
            window.addEventListener("resize", this.updateScrollableOverflow);
          }
        }

        this.container?.addEventListener("click", this.handleContainerClick, true);

        document.addEventListener("facets:change", this.syncActiveLinks);
        document.addEventListener("facet:urlupdated", this.syncActiveLinks);
        window.addEventListener("popstate", this.syncActiveLinks);
        this.localizeLinkHrefs();
        queueMicrotask(() => {
          this.syncActiveLinks();
          this.updateScrollableOverflow();
          this.checkScroll();
        });
      }

      localizeLinkHrefs() {
        const root = window.Shopify?.routes?.root || "/";
        if (root === "/") return;
        const rootPath = root.replace(/\/$/, "");
        this.querySelectorAll("a.secondary-navigation__link").forEach((link) => {
          const href = link.getAttribute("href");
          if (!href) return;
          let url;
          try {
            url = new URL(href, window.location.origin);
          } catch {
            return;
          }
          if (url.origin !== window.location.origin) return;
          if (
            url.pathname === rootPath ||
            url.pathname.startsWith(`${rootPath}/`)
          ) {
            return;
          }
          url.pathname = `${rootPath}${url.pathname}`;
          link.setAttribute("href", `${url.pathname}${url.search}${url.hash}`);
        });
      }

      /**
       * Keep pills in sync with the real URL. Server Liquid can miss active state when
       * collection.filters omits zero-result values, and facets AJAX updates the URL without a re-render.
       */
      syncActiveLinks() {
        const pathname = window.location.pathname;
        const search = window.location.search;
        const currentHandle = collectionHandleFromPathname(pathname);
        const currentFq = normalizedFilterQuery(search);

        this.querySelectorAll("li.secondary-navigation__item").forEach((item) => {
          const link = item.querySelector("a.secondary-navigation__link");
          if (!link) return;
          const href = link.getAttribute("href");
          if (!href) return;
          let url;
          try {
            url = new URL(href, window.location.origin);
          } catch {
            return;
          }
          const linkHandle = collectionHandleFromPathname(url.pathname);
          const linkFq = url.search ? normalizedFilterQuery(url.search) : "";

          const pathOk =
            Boolean(currentHandle && linkHandle && currentHandle === linkHandle);
          // Faceted pills (filter.* in href) must match the URL exactly. Plain collection
          // pills stay active when the shopper adds facets (e.g. size) on the same collection.
          const filtersOk =
            currentFq === linkFq || (linkFq === "" && pathOk);
          const active = pathOk && filtersOk;

          link.classList.toggle("secondary-navigation__link--active", active);

          const dismiss = item.querySelector("[data-secondary-nav-dismiss]");
          if (dismiss) {
            dismiss.hidden = !(active && linkFq !== "");
          }
        });
        this.updateScrollableOverflow();
        this.checkScroll();
      }

      handleContainerClick(e) {
        const dismiss = e.target.closest("[data-secondary-nav-dismiss]");
        if (dismiss && !dismiss.hidden) {
          e.preventDefault();
          e.stopPropagation();
          window.location.assign(hrefWithoutFilterParams());
          return;
        }

        const link = e.target.closest("a[data-preserve-filters]");
        if (!link || !window.location.search) return;
        e.preventDefault();
        const url = new URL(link.href);
        url.search = window.location.search;
        url.searchParams.delete("page");
        window.location.href = url.toString();
      }

      updateScrollableOverflow() {
        if (!this.scrollableElement || !this.container) return;
        if (!this.container.classList.contains("mobile-scroll-hint--fade")) {
          return;
        }
        const el = this.scrollableElement;
        const overflow = el.scrollWidth > el.clientWidth + 1;
        this.container.classList.toggle("has-scroll-overflow", overflow);
      }

      checkScroll(e) {
        const el = this.scrollableElement;
        if (!el || !this.container) return;

        const maxX = Math.max(0, el.scrollWidth - el.clientWidth);
        const sl = Math.abs(Math.round(el.scrollLeft));
        const atEnd = maxX <= 1 || Math.abs(maxX - sl) <= 1;
        this.container.classList.toggle("at-max-scroll", atEnd);

        if (sl > 0) {
          this.container.classList.add("not-at-min-scroll");
        } else {
          this.container.classList.remove("not-at-min-scroll");
        }
      }

      disconnectedCallback() {
        document.removeEventListener("facets:change", this.syncActiveLinks);
        document.removeEventListener("facet:urlupdated", this.syncActiveLinks);
        window.removeEventListener("popstate", this.syncActiveLinks);
        window.removeEventListener("resize", this.updateScrollableOverflow);
        this.resizeObserver?.disconnect();
        if (this.scrollableElement && this.container) {
          this.scrollableElement.removeEventListener(
            "scroll",
            this.checkScroll,
          );
        }
        this.container?.removeEventListener("click", this.handleContainerClick, true);
      }
    },
  );
}
